import { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useUploadFiles } from '@/hooks/upload-files/useUploadFiles';
import { VerticalContainer } from '@/components/containers/VerticalContainer';
import { R } from '@/Resources.resx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCreateDietitianUploadConsultation } from '@/features/fileUpload/hooks/useCreateDietitianUploadConsultation';
import { ConsultationTypeEnum } from '@/shared/enums';
import { Button, Modal } from '@/components';
import { Card } from '@/components/Card';
import { InlineAlert } from '@/components/InlineAlert';
import { AttachmentsList } from '@/hooks/upload-files/AttachmentsList';
import { UploadFilesButton } from '@/hooks/upload-files/UploadFilesButton';

type FileUploadProps = {
    fullName: string;
    cprNumber: string;
};

export function FileUploadDietitianApp(
    containerQuery: string,
    fileUploadProps: FileUploadProps,
) {
    const elm = document.querySelector(containerQuery);
    const root = createRoot(elm);
    const queryClient = new QueryClient();
    root.render(
        <QueryClientProvider client={queryClient}>
            <FileUpload {...fileUploadProps} />
        </QueryClientProvider>,
    );
}

const FileUpload = ({ fullName, cprNumber }: FileUploadProps) => {
    const { handleFileChange, files, removeFileFromList } = useUploadFiles({
        senderType: 'PATIENT',
        fileConstraints: {
            maxFileSizeMb: 30,
        },
    });

    const fileUIDs = files.map((file) => file.guid);

    const allFilesUploaded = files.every((file) => file.isUploaded);

    const { mutate, status, reset, errorMessage } =
        useCreateDietitianUploadConsultation();

    const [uploadFailedModal, setUploadFailedModal] = useState<boolean>(false);

    const closeUploadFailedModal = () => {
        setUploadFailedModal(false);
    };

    const handleClickSendFiles = () => {
        mutate(
            {
                FromFullName: fullName,
                CprNumber: cprNumber,
                FileUids: fileUIDs,
                ConsultationType:
                    ConsultationTypeEnum.DietitianUploadAttachmentMessage,
                Subject: 'Send files to the dietitian',
                Message: R.DietitianUploadAttachmentConsultationMessage,
            },
            {
                onError: () => {
                    reset();
                    setUploadFailedModal(true);
                },
            },
        );
    };

    return (
        <>
            {status !== 'success' && (
                <Card className={'max-w-[45rem]'}>
                    <h2 className={'mb-4'}>{R.SendFilesToDietitianHeader}</h2>
                    <span>{R.AllowedFileFormatsDietitian}</span>
                    {files.length > 0 && (
                        <VerticalContainer>
                            <AttachmentsList
                                files={files}
                                onRemoveCallback={removeFileFromList}
                            />
                        </VerticalContainer>
                    )}
                    <div className={'flex justify-center'}>
                        <span className={'mt-4 w-48'}>
                            <UploadFilesButton
                                onFileChange={handleFileChange}
                                accept="image/jpeg,image/png,image/gif,.pdf,.mov"
                            />
                        </span>
                    </div>

                    <div className={'flex justify-end'}>
                        <Button
                            variant="primary"
                            disabled={!(files.length > 0) || !allFilesUploaded}
                            className={
                                status === 'pending'
                                    ? 'button--loading mt-4'
                                    : 'mt-4'
                            }
                            onClick={handleClickSendFiles}
                        >
                            {R.UploadFilesButton}
                        </Button>
                    </div>
                </Card>
            )}
            {status === 'success' && (
                <Card className={'max-w-[30rem]'}>
                    <div>
                        <h2>{R.UploadFilesDietitianSuccess}</h2>
                        <footer className="mt-12 text-right">
                            <Button
                                variant="primary"
                                onClick={() => {
                                    window.location.href = '/';
                                }}
                            >
                                {R.UploadFilesDietitianFinish}
                            </Button>
                        </footer>
                    </div>
                </Card>
            )}
            {uploadFailedModal && (
                <Modal onClose={closeUploadFailedModal}>
                    {status === 'error' && (
                        <InlineAlert
                            onClose={closeUploadFailedModal}
                            errorMessage={errorMessage}
                        />
                    )}
                </Modal>
            )}
        </>
    );
};
